import { useState } from "react";
import {
  BookmarkCheck,
  CircleAlertIcon,
  CircleCheck,
  MoonIcon,
  Popcorn,
  SunIcon,
} from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "~/components/ui/alert";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Checkbox } from "~/components/ui/checkbox";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { RadioGroup, RadioGroupIndicator } from "~/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { Slider } from "~/components/ui/slider";
import { Switch } from "~/components/ui/switch";
import { cn } from "~/lib/utils";
import { StepperTest } from "./StepperTest";

const ShadcnTest = () => {
  const [inputValue, setInputValue] = useState("");
  const [checked, setChecked] = useState(false);
  const [radioValue, setRadioValue] = useState("option-1");
  const [selectValue, setSelectValue] = useState("");
  const [sliderValue, setSliderValue] = useState([50]);
  const [rangeSliderValue, setRangeSliderValue] = useState([25, 75]);
  const [switchValue, setSwitchValue] = useState(false);
  const [airplaneMode, setAirplaneMode] = useState(false);
  const [notifications, setNotifications] = useState(true);

  const [darkMode, setDarkMode] = useState(true);

  return (
    <div className={cn("container mx-auto p-8 max-w-4xl", darkMode && "dark")}>
      <h1 className="text-4xl font-bold mb-8">Shadcn/UI Test Page</h1>

      <div className="space-y-8">
        {/* Buttons Section */}
        <Card>
          <CardHeader>
            <CardTitle>Button Components</CardTitle>
            <CardDescription>Various button styles from shadcn/ui</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap gap-4">
              <Button>Default</Button>
              <Button variant="secondary">Secondary</Button>
              <Button variant="destructive">Destructive</Button>
              <Button variant="outline">Outline</Button>
              <Button variant="ghost">Ghost</Button>
              <Button variant="link">Link</Button>
            </div>

            <div className="flex flex-wrap gap-4">
              <Button size="sm">Small</Button>
              <Button size="default">Default</Button>
              <Button size="lg">Large</Button>
            </div>

            <div className="flex flex-wrap gap-4">
              <Button disabled>Disabled</Button>
              <Button className="w-full">Full Width</Button>
            </div>
          </CardContent>
        </Card>

        {/* Form Components Section */}
        <Card>
          <CardHeader>
            <CardTitle>Form Components</CardTitle>
            <CardDescription>Input fields and form controls from shadcn/ui</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Input Fields */}
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
              />
              <p className="text-sm text-muted-foreground">
                Current value: {inputValue || "Empty"}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" placeholder="Enter password" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="disabled">Disabled Input</Label>
              <Input id="disabled" disabled placeholder="This input is disabled" />
            </div>

            {/* Checkbox */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="terms"
                checked={checked}
                onCheckedChange={(checked) => setChecked(checked as boolean)}
              />
              <Label
                htmlFor="terms"
                className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
              >
                Accept terms and conditions
              </Label>
            </div>

            {/* Radio Group */}
            <div className="space-y-2">
              <Label>Choose an option</Label>
              <RadioGroup value={radioValue} onValueChange={setRadioValue}>
                <div className="flex items-center space-x-2">
                  <RadioGroupIndicator value="option-1" id="option-1" />
                  <Label htmlFor="option-1">Option 1</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupIndicator value="option-2" id="option-2" />
                  <Label htmlFor="option-2">Option 2</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupIndicator value="option-3" id="option-3" />
                  <Label htmlFor="option-3">Option 3</Label>
                </div>
              </RadioGroup>
              <p className="text-sm text-muted-foreground">Selected: {radioValue}</p>
            </div>

            {/* Select */}
            <div className="space-y-2">
              <Label htmlFor="framework">Select Framework</Label>
              <Select value={selectValue} onValueChange={setSelectValue}>
                <SelectTrigger id="framework">
                  <SelectValue placeholder="Select a framework" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="react">React</SelectItem>
                  <SelectItem value="vue">Vue</SelectItem>
                  <SelectItem value="angular">Angular</SelectItem>
                  <SelectItem value="svelte">Svelte</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground">Selected: {selectValue || "None"}</p>
            </div>
          </CardContent>
        </Card>

        {/* Slider Section */}
        <Card>
          <CardHeader>
            <CardTitle>Slider Components</CardTitle>
            <CardDescription>Range inputs with smooth interactions</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Basic Slider */}
            <div className="space-y-2">
              <Label>Basic Slider</Label>
              <Slider
                value={sliderValue}
                onValueChange={setSliderValue}
                max={100}
                step={1}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>0</span>
                <span>Current: {sliderValue[0]}</span>
                <span>100</span>
              </div>
            </div>

            {/* Range Slider */}
            <div className="space-y-2">
              <Label>Range Slider (Multiple Values)</Label>
              <Slider
                value={rangeSliderValue}
                onValueChange={setRangeSliderValue}
                max={100}
                step={5}
                className="w-full"
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>0</span>
                <span>
                  Range: {rangeSliderValue[0]} - {rangeSliderValue[1]}
                </span>
                <span>100</span>
              </div>
            </div>

            {/* Custom Step Slider */}
            <div className="space-y-2">
              <Label>Volume Control (Steps of 10)</Label>
              <Slider defaultValue={[30]} max={100} step={10} className="w-full" />
              <p className="text-sm text-muted-foreground">
                Drag to adjust volume in increments of 10
              </p>
            </div>

            {/* Disabled Slider */}
            <div className="space-y-2">
              <Label>Disabled Slider</Label>
              <Slider defaultValue={[65]} max={100} disabled className="w-full" />
              <p className="text-sm text-muted-foreground">This slider is disabled</p>
            </div>

            {/* Practical Example */}
            <div className="space-y-4 pt-4 border-t">
              <Label>Price Range Filter</Label>
              <div className="space-y-3">
                <Slider
                  defaultValue={[20, 80]}
                  max={200}
                  step={5}
                  className="w-full"
                  onValueChange={(values) => {
                    // In a real app, this would filter products
                    console.log(`Filtering products: $${values[0]} - $${values[1]}`);
                  }}
                />
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Min:</span>
                    <Badge variant="secondary">$20</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">Max:</span>
                    <Badge variant="secondary">$80</Badge>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Switch Section */}
        <Card>
          <CardHeader>
            <CardTitle>Switch Components</CardTitle>
            <CardDescription>Toggle switches for boolean states</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Basic Switch */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Basic Switch</Label>
                <p className="text-sm text-muted-foreground">
                  Current state: {switchValue ? "On" : "Off"}
                </p>
              </div>
              <Switch checked={switchValue} onCheckedChange={setSwitchValue} />
            </div>

            {/* Airplane Mode Example */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Airplane Mode</Label>
                <p className="text-sm text-muted-foreground">
                  {airplaneMode
                    ? "All wireless connections disabled"
                    : "Wireless connections enabled"}
                </p>
              </div>
              <Switch checked={airplaneMode} onCheckedChange={setAirplaneMode} />
            </div>

            {/* Notifications Example */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Push Notifications</Label>
                <p className="text-sm text-muted-foreground">
                  {notifications ? "Receiving notifications" : "Notifications disabled"}
                </p>
              </div>
              <Switch checked={notifications} onCheckedChange={setNotifications} />
            </div>

            {/* Disabled Switch */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Disabled Switch</Label>
                <p className="text-sm text-muted-foreground">This switch is disabled</p>
              </div>
              <Switch disabled />
            </div>

            {/* Switch with Badge */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <div className="flex items-center gap-2">
                  <Label>Dark Mode</Label>
                  <Badge variant="secondary">Beta</Badge>
                </div>
                <p className="text-sm text-muted-foreground">Toggle dark mode appearance</p>
              </div>
              <Switch />
            </div>
          </CardContent>
        </Card>

        {/* Badges Section */}
        <Card>
          <CardHeader>
            <CardTitle>Badge Components</CardTitle>
            <CardDescription>Various badge styles from shadcn/ui</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              <Badge>Default</Badge>
              <Badge variant="secondary">Secondary</Badge>
              <Badge variant="destructive">Destructive</Badge>
              <Badge variant="outline">Outline</Badge>
            </div>
          </CardContent>
        </Card>

        {/* Card Examples */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader>
              <CardTitle>Card Example 1</CardTitle>
              <CardDescription>This is a card description</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Cards are used to group related content and actions about a single subject.
              </p>
            </CardContent>
            <CardFooter>
              <Button className="w-full">Learn More</Button>
            </CardFooter>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Card Example 2</CardTitle>
              <CardDescription>Another card with badges</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <p className="text-sm">This card demonstrates badges:</p>
                <div className="flex gap-2">
                  <Badge>Feature</Badge>
                  <Badge variant="secondary">New</Badge>
                  <Badge variant="outline">Beta</Badge>
                </div>
              </div>
            </CardContent>
            <CardFooter className="flex justify-between">
              <Button variant="outline">Cancel</Button>
              <Button>Confirm</Button>
            </CardFooter>
          </Card>
        </div>

        {/* Interactive Demo */}
        <Card>
          <CardHeader>
            <CardTitle>Interactive Demo</CardTitle>
            <CardDescription>Click the button to see shadcn/ui in action</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-4">
              <Button onClick={() => alert("Primary button clicked!")}>Click Me</Button>
              <Button
                variant="secondary"
                onClick={() => console.log("Secondary button clicked - check console!")}
              >
                Log to Console
              </Button>
            </div>
            <p className="text-sm text-muted-foreground">
              These buttons demonstrate event handling with shadcn/ui components.
            </p>
          </CardContent>
        </Card>

        {/* Typography Examples */}
        <Card>
          <CardHeader>
            <CardTitle>Typography with Tailwind</CardTitle>
            <CardDescription>Using Tailwind CSS classes for typography</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <h2 className="text-2xl font-semibold">Heading 2</h2>
            <h3 className="text-xl font-medium">Heading 3</h3>
            <p className="text-base">Regular paragraph text with default styling.</p>
            <p className="text-sm text-muted-foreground">Small muted text for descriptions.</p>
            <p className="text-xs text-muted-foreground">Extra small text for fine print.</p>
          </CardContent>
        </Card>

        {/* Brand Colors Section */}
        <Card>
          <CardHeader>
            <CardTitle>Brand Colors from Design Tokens</CardTitle>
            <CardDescription>
              These colors are automatically applied from the design system
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <div className="h-20 bg-primary rounded-md flex items-center justify-center">
                  <span className="text-primary-foreground text-sm font-medium">Primary</span>
                </div>
                <p className="text-xs text-muted-foreground text-center">#613DE4</p>
              </div>

              <div className="space-y-2">
                <div className="h-20 bg-destructive rounded-md flex items-center justify-center">
                  <span className="text-destructive-foreground text-sm font-medium">
                    Destructive
                  </span>
                </div>
                <p className="text-xs text-muted-foreground text-center">#E00037</p>
              </div>

              <div className="space-y-2">
                <div className="h-20 bg-secondary rounded-md flex items-center justify-center">
                  <span className="text-secondary-foreground text-sm font-medium">Secondary</span>
                </div>
                <p className="text-xs text-muted-foreground text-center">Neutral 100</p>
              </div>

              <div className="space-y-2">
                <div className="h-20 bg-muted rounded-md flex items-center justify-center">
                  <span className="text-muted-foreground text-sm font-medium">Muted</span>
                </div>
                <p className="text-xs text-muted-foreground text-center">Neutral 100</p>
              </div>
            </div>

            <div className="mt-6">
              <p className="text-sm font-medium mb-3">Chart Colors (from Status Tokens)</p>
              <div className="flex gap-2">
                <div
                  className="h-12 w-12 rounded"
                  style={{ backgroundColor: "var(--chart-1)" }}
                  title="Primary"
                />
                <div
                  className="h-12 w-12 rounded"
                  style={{ backgroundColor: "var(--chart-2)" }}
                  title="Secondary"
                />
                <div
                  className="h-12 w-12 rounded"
                  style={{ backgroundColor: "var(--chart-3)" }}
                  title="Info"
                />
                <div
                  className="h-12 w-12 rounded"
                  style={{ backgroundColor: "var(--chart-4)" }}
                  title="Running"
                />
                <div
                  className="h-12 w-12 rounded"
                  style={{ backgroundColor: "var(--chart-5)" }}
                  title="Secured"
                />
              </div>
            </div>

            <div className="mt-4 p-4 bg-muted rounded-md">
              <p className="text-sm">
                <strong>Note:</strong> All these colors are automatically synchronized from{" "}
                <code className="text-xs bg-background px-1 py-0.5 rounded">
                  design-system/tokens/tokens.json
                </code>{" "}
                through Style Dictionary.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Alert Examples */}
        <Card>
          <CardHeader>
            <CardTitle>Alert Components</CardTitle>
            <CardDescription>Displays a callout for user attention.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <CircleCheck />
              <AlertTitle>Success! Your changes have been saved.</AlertTitle>
              <AlertDescription>
                This is an alert with icon, title and description.
              </AlertDescription>
            </Alert>
            <Alert>
              <BookmarkCheck />
              <AlertDescription>
                This one has an icon and a description only. No title.
              </AlertDescription>
            </Alert>
            <Alert>
              <AlertDescription>
                This one has a description only. No title. No icon.
              </AlertDescription>
            </Alert>
            <Alert>
              <Popcorn />
              <AlertTitle>{"Let's try one with icon and title"}</AlertTitle>
            </Alert>
            <Alert>
              <CircleAlertIcon />
              <AlertTitle>
                This is an extremely long alert title that spans multiple lines to demonstrate
                how...
              </AlertTitle>
              <AlertDescription>
                This is an equally long description that contains detailed information about the
                alert. It shows how the component can accommodate extensive content while preserving
                proper spacing, alignment, and readability across different screen sizes and
                viewport widths. This helps ensure the user experience remains consistent regardless
                of the content length.
              </AlertDescription>
            </Alert>
            <Alert variant="destructive">
              <CircleAlertIcon />
              <AlertTitle>Something went wrong!</AlertTitle>
              <AlertDescription>Your session has expired. Please log in again.</AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        {/* Stepper Example */}
        <Card>
          <StepperTest />
        </Card>
      </div>

      <Button
        size="icon"
        variant="outline"
        className="fixed bottom-10 right-10"
        onClick={() => setDarkMode(!darkMode)}
      >
        {darkMode ? <MoonIcon className="size-5" /> : <SunIcon className="size-5" />}
      </Button>
    </div>
  );
};

export default ShadcnTest;
